import React from "react";
import { Styled } from "./styles";
import { i18n } from "@/utils/i18n";
import constant from "@/utils/constant";
import { Divider, Grid2, Typography } from "@mui/material";
import SectionTitle from "@/components/ui/section-title";
import { useFieldArray, useFormContext } from "react-hook-form";
import { Forms } from "@/types/form";
import Comment from "../fields/comment";
import Status from "../fields/status";
import { useBalances } from "./hooks";

interface Props {
    startDate: string;
    endDate: string;
}

const AssetsAndLiabilities: React.FC<Props> = ({ startDate, endDate }) => {
    const { CURRENCIES } = constant;

    const { control } = useFormContext<Forms["Report"]>();
    const { fields: accounts } = useFieldArray({
        name: "accounts",
        control,
    });
    const { fields: assets } = useFieldArray({ name: "assets", control });
    const { fields: debts } = useFieldArray({ name: "debts", control });
    const { accountsBalances, assetsBalances, debtsBalances } = useBalances({
        accounts,
        assets,
        debts,
    });

    return (
        <Styled.Container>
            <SectionTitle title={i18n.t("assetsAndLiabilities")} />
            <Grid2 container padding="0px 60px" direction="column">
                <Grid2 container direction="column" rowSpacing={1.5}>
                    <Grid2 size={10}>
                        <Typography variant="h5" fontWeight="bold">
                            {i18n.t("transactionAccounts")}
                        </Typography>
                    </Grid2>
                    <Grid2 container size={12} columnSpacing={4}>
                        <Grid2 size={2}>
                            <Typography variant="h6" fontWeight="bold">
                                {i18n.t("account")}
                            </Typography>
                        </Grid2>
                        <Grid2 size={2}>
                            <Typography variant="h6" fontWeight="bold">
                                {i18n.t("accountNumber")}
                            </Typography>
                        </Grid2>
                        <Grid2 size={1.5}>
                            <Typography variant="h6" fontWeight="bold" textAlign="right">
                                {Intl.DateTimeFormat("sv-SE").format(new Date(startDate))}
                            </Typography>
                        </Grid2>
                        <Grid2 size={1.5}>
                            <Typography variant="h6" fontWeight="bold" textAlign="right">
                                {Intl.DateTimeFormat("sv-SE").format(new Date(endDate))}
                            </Typography>
                        </Grid2>
                        <Grid2 size={3}>
                            <Typography variant="h6" fontWeight="bold" textAlign="left">
                                {i18n.t("comment")}
                            </Typography>
                        </Grid2>
                        <Grid2 size={2}>
                            <Typography variant="h6" fontWeight="bold" textAlign="center">
                                {i18n.t("status")}
                            </Typography>
                        </Grid2>
                        <Grid2 size={12}>
                            <Divider />
                        </Grid2>
                    </Grid2>
                    {accounts.map(({ id, ...field }, index) => {
                        const { description, startAmount, endAmount, note } = field;
                        return (
                            <Grid2
                                container
                                size={12}
                                columnSpacing={4}
                                key={id}
                                direction="row"
                                alignItems="center"
                            >
                                <Grid2 size={2}>
                                    <Typography variant="subtitle1">{description}</Typography>
                                </Grid2>
                                <Grid2 size={2}>
                                    <Typography variant="subtitle1">{note}</Typography>
                                </Grid2>
                                <Grid2 size={1.5}>
                                    <Typography variant="subtitle1" textAlign="right">
                                        {CURRENCIES.KRONA.format(startAmount)}
                                    </Typography>
                                </Grid2>
                                <Grid2 size={1.5}>
                                    <Typography variant="subtitle1" textAlign="right">
                                        {CURRENCIES.KRONA.format(endAmount)}
                                    </Typography>
                                </Grid2>
                                <Grid2 size={3}>
                                    <Comment index={index} variant="accounts" />
                                </Grid2>
                                <Grid2
                                    size={2}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    <Status index={index} variant="accounts" />
                                </Grid2>
                                <Grid2 size={12}>
                                    <Divider />
                                </Grid2>
                            </Grid2>
                        );
                    })}
                    <Grid2 container size={12} columnSpacing={4}>
                        <Grid2 size={4}></Grid2>
                        <Grid2 size={1.5}>
                            <Typography variant="h6" fontWeight="bold" textAlign="right">
                                {i18n.t("sumA")}{" "}
                                {CURRENCIES.KRONA.format(accountsBalances.startBalance.toNumber())}
                            </Typography>
                        </Grid2>
                        <Grid2 size={1.5}>
                            <Typography variant="h6" fontWeight="bold" textAlign="right">
                                {i18n.t("sumD")}{" "}
                                {CURRENCIES.KRONA.format(accountsBalances.endBalance.toNumber())}
                            </Typography>
                        </Grid2>
                    </Grid2>
                </Grid2>
                <Grid2 container direction="column" rowSpacing={1.5}>
                    <Grid2 size={12}>
                        <Typography variant="h5" fontWeight="bold">
                            {i18n.t("otherBankAccounts")}
                        </Typography>
                    </Grid2>
                    <Grid2 container size={12} columnSpacing={4}>
                        <Grid2 size={2}>
                            <Typography variant="h6" fontWeight="bold">
                                {i18n.t("type")}
                            </Typography>
                        </Grid2>
                        <Grid2 size={2}>
                            <Typography variant="h6" fontWeight="bold">
                                {i18n.t("description")}
                            </Typography>
                        </Grid2>
                        <Grid2 size={1.5}>
                            <Typography variant="h6" fontWeight="bold" textAlign="right">
                                {Intl.DateTimeFormat("sv-SE").format(new Date(startDate))}
                            </Typography>
                        </Grid2>
                        <Grid2 size={1.5}>
                            <Typography variant="h6" fontWeight="bold" textAlign="right">
                                {Intl.DateTimeFormat("sv-SE").format(new Date(endDate))}
                            </Typography>
                        </Grid2>
                        <Grid2 size={3}>
                            <Typography variant="h6" fontWeight="bold" textAlign="left">
                                {i18n.t("comment")}
                            </Typography>
                        </Grid2>
                        <Grid2 size={2}>
                            <Typography variant="h6" fontWeight="bold" textAlign="center">
                                {i18n.t("status")}
                            </Typography>
                        </Grid2>
                        <Grid2 size={12}>
                            <Divider />
                        </Grid2>
                    </Grid2>

                    {assets.map(({ id, ...field }, index) => {
                        const { description, startAmount, endAmount, note } = field;
                        return (
                            <Grid2
                                container
                                size={12}
                                columnSpacing={4}
                                key={id}
                                direction="row"
                                alignItems="center"
                            >
                                <Grid2 size={2}>
                                    <Typography variant="subtitle1">{description}</Typography>
                                </Grid2>
                                <Grid2 size={2}>
                                    <Typography variant="subtitle1">{note}</Typography>
                                </Grid2>
                                <Grid2 size={1.5}>
                                    <Typography variant="subtitle1" textAlign="right">
                                        {CURRENCIES.KRONA.format(startAmount)}
                                    </Typography>
                                </Grid2>
                                <Grid2 size={1.5}>
                                    <Typography variant="subtitle1" textAlign="right">
                                        {CURRENCIES.KRONA.format(endAmount)}
                                    </Typography>
                                </Grid2>
                                <Grid2 size={3}>
                                    <Comment index={index} variant="assets" />
                                </Grid2>
                                <Grid2
                                    size={2}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    <Status index={index} variant="assets" />
                                </Grid2>
                                <Grid2 size={12}>
                                    <Divider />
                                </Grid2>
                            </Grid2>
                        );
                    })}
                    <Grid2 container size={12} columnSpacing={4}>
                        <Grid2 size={4}></Grid2>
                        <Grid2 size={1.5}>
                            <Typography variant="h6" fontWeight="bold" textAlign="right">
                                {CURRENCIES.KRONA.format(assetsBalances.startBalance.toNumber())}
                            </Typography>
                        </Grid2>
                        <Grid2 size={1.5}>
                            <Typography variant="h6" fontWeight="bold" textAlign="right">
                                {CURRENCIES.KRONA.format(assetsBalances.endBalance.toNumber())}
                            </Typography>
                        </Grid2>
                    </Grid2>

                    <Grid2 size={10} marginTop={3}>
                        <Typography variant="h5" fontWeight="bold">
                            {i18n.t("debts")}
                        </Typography>
                    </Grid2>
                    <Grid2 container size={12} columnSpacing={4}>
                        <Grid2 size={2}>
                            <Typography variant="h6" fontWeight="bold">
                                {i18n.t("type")}
                            </Typography>
                        </Grid2>
                        <Grid2 size={2}>
                            <Typography variant="h6" fontWeight="bold">
                                {i18n.t("description")}
                            </Typography>
                        </Grid2>
                        <Grid2 size={1.5}>
                            <Typography variant="h6" fontWeight="bold" textAlign="right">
                                {Intl.DateTimeFormat("sv-SE").format(new Date(startDate))}
                            </Typography>
                        </Grid2>
                        <Grid2 size={1.5}>
                            <Typography variant="h6" fontWeight="bold" textAlign="right">
                                {Intl.DateTimeFormat("sv-SE").format(new Date(endDate))}
                            </Typography>
                        </Grid2>
                        <Grid2 size={3}>
                            <Typography variant="h6" fontWeight="bold" textAlign="left">
                                {i18n.t("comment")}
                            </Typography>
                        </Grid2>
                        <Grid2 size={2}>
                            <Typography variant="h6" fontWeight="bold" textAlign="center">
                                {i18n.t("status")}
                            </Typography>
                        </Grid2>
                        <Grid2 size={12}>
                            <Divider />
                        </Grid2>
                    </Grid2>

                    {debts.map(({ id, ...field }, index) => {
                        const { description, startAmount, endAmount, note } = field;
                        return (
                            <Grid2
                                container
                                size={12}
                                columnSpacing={4}
                                key={id}
                                direction="row"
                                alignItems="center"
                            >
                                <Grid2 size={2}>
                                    <Typography variant="subtitle1">{description}</Typography>
                                </Grid2>
                                <Grid2 size={2}>
                                    <Typography variant="subtitle1">{note}</Typography>
                                </Grid2>
                                <Grid2 size={1.5}>
                                    <Typography variant="subtitle1" textAlign="right">
                                        {CURRENCIES.KRONA.format(startAmount)}
                                    </Typography>
                                </Grid2>
                                <Grid2 size={1.5}>
                                    <Typography variant="subtitle1" textAlign="right">
                                        {CURRENCIES.KRONA.format(endAmount)}
                                    </Typography>
                                </Grid2>
                                <Grid2 size={3}>
                                    <Comment index={index} variant="debts" />
                                </Grid2>
                                <Grid2
                                    size={2}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    <Status index={index} variant="debts" />
                                </Grid2>
                                <Grid2 size={12}>
                                    <Divider />
                                </Grid2>
                            </Grid2>
                        );
                    })}
                    <Grid2 container size={12} columnSpacing={4}>
                        <Grid2 size={4}></Grid2>
                        <Grid2 size={1.5}>
                            <Typography variant="h6" fontWeight="bold" textAlign="right">
                                {CURRENCIES.KRONA.format(debtsBalances.startBalance.toNumber())}
                            </Typography>
                        </Grid2>
                        <Grid2 size={1.5}>
                            <Typography variant="h6" fontWeight="bold" textAlign="right">
                                {CURRENCIES.KRONA.format(debtsBalances.endBalance.toNumber())}
                            </Typography>
                        </Grid2>
                    </Grid2>
                </Grid2>
            </Grid2>
        </Styled.Container>
    );
};

export default AssetsAndLiabilities;
